import React, { useContext, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  Typography,
  useTheme,
} from "@mui/material";

import ResponsiveTextField from "components/styled/ResponsiveTextField";

import { PAGES } from "utils/constants";
import { PageContext } from "utils/PageContext";
import { UserContext } from "utils/UserContext";

import PropTypes from "prop-types";

function UserForm({ open, handleCloseForm }) {
  const { user, setUser } = useContext(UserContext);
  const { setPage } = useContext(PageContext);

  const mainTheme = useTheme();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
    },
  });

  const onSubmit = async (data) => {
    setIsSubmitting(true);
    await setUser({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
    });

    // Simulate async operation
    await new Promise((resolve) => setTimeout(resolve, 1000));

    handleCloseForm();
    setIsSubmitting(false);
    setPage(PAGES.FINALSCORE);
  };

  useEffect(() => {
    if (
      user.firstName.length >= 1 &&
      user.lastName.length >= 1 &&
      user.email.length > 1
    ) {
      window.sessionStorage.setItem(
        "FirstName",
        JSON.stringify(user.firstName)
      );
      window.sessionStorage.setItem("LastName", JSON.stringify(user.lastName));
      window.sessionStorage.setItem("Email", JSON.stringify(user.email));

      setValue("firstName", user.firstName);
      setValue("lastName", user.lastName);
      setValue("email", user.email);
      setIsSubmitting(false);
    }
  }, [user]);

  return (
    <Dialog open={open} onClose={handleCloseForm}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          bgcolor: mainTheme.palette.blackSecondary.main,
          borderRadius: "1.50rem",
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: mainTheme.palette.blackSecondary.main,
            color: mainTheme.palette.whitePrimary.main,
          }}
        >
          You are so close to getting your COMP Score!
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: mainTheme.palette.blackSecondary.main,
            color: mainTheme.palette.whitePrimary.main,
          }}
        >
          <Box
            component="form"
            method="post"
            name="contact"
            action="/contact"
            sx={{
              display: "flex",
              flexDirection: "column",
              bgcolor: mainTheme.palette.blackSecondary.main,
            }}
            onSubmit={handleSubmit(onSubmit)}
          >
            {/* Hidden input for Netlify form handling */}
            <input type="hidden" name="form-name" value="contact" />

            <Grid container alignItems="center" direction="column" spacing={2}>
              <Grid item xs={6} md={8}>
                <Controller
                  control={control}
                  name="firstName"
                  rules={{
                    required: {
                      value: true,
                      message: "First Name is Required",
                    },
                  }}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel
                        disableAnimation
                        htmlFor="first-name-input"
                        sx={{
                          color: "transparent",
                        }}
                      >
                        First Name
                      </InputLabel>
                      <ResponsiveTextField
                        variant="outlined"
                        {...field}
                        required
                        type="text"
                        id="first-name-input"
                        placeholder="First Name"
                        error={!!errors.firstName}
                      />
                    </FormControl>
                  )}
                />
                <Typography variant="inherit" color="textSecondary">
                  {errors.firstName?.message}
                </Typography>
              </Grid>

              <Grid item xs={6} md={8}>
                <Controller
                  control={control}
                  name="lastName"
                  rules={{
                    required: {
                      value: true,
                      message: "Last Name is Required",
                    },
                  }}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel
                        disableAnimation
                        htmlFor="last-name-input"
                        sx={{
                          color: "transparent",
                        }}
                      />
                      <ResponsiveTextField
                        variant="outlined"
                        {...field}
                        required
                        type="text"
                        placeholder="Last Name"
                        id="last-name-input"
                        error={!!errors.lastName}
                      />
                    </FormControl>
                  )}
                />
                <Typography variant="inherit" color="textSecondary">
                  {errors.lastName?.message}
                </Typography>
              </Grid>

              <Grid item xs={6} md={8}>
                <Controller
                  control={control}
                  name="email"
                  rules={{
                    required: {
                      value: true,
                      message: "Email is Required",
                    },
                    pattern: {
                      value: /\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/,
                      message: "Email is Invalid",
                    },
                  }}
                  render={({ field }) => (
                    <FormControl>
                      <InputLabel
                        disableAnimation
                        htmlFor="email-input"
                        sx={{
                          color: "transparent",
                        }}
                      >
                        Email
                      </InputLabel>
                      <ResponsiveTextField
                        variant="outlined"
                        {...field}
                        required
                        type="email"
                        placeholder="Email"
                        id="email-input"
                        error={!!errors.email}
                      />
                    </FormControl>
                  )}
                />
                <Typography variant="inherit" color="textSecondary">
                  {errors.email?.message}
                </Typography>
              </Grid>

              <Grid item>
                <Button
                  disabled={isSubmitting}
                  sx={{
                    bgcolor: mainTheme.palette.darkBluePrimary.main,
                  }}
                  type="submit"
                >
                  See My Score
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Box>
    </Dialog>
  );
}

UserForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleCloseForm: PropTypes.func.isRequired,
};

export default UserForm;
